/**
 * Solar position and day length from the NOAA solar calculator equations, accurate to well under
 * a degree and about a minute, which is all a footer readout needs. Angles are degrees, azimuth is
 * measured clockwise from north, and every time is a real instant (a Date or epoch milliseconds).
 */

const RAD = Math.PI / 180
const DAY_MS = 86400000
const HORIZON = -0.833

export interface SunPosition {
    altitude: number
    azimuth: number
}

export interface SunPathPoint {
    hour: number
    altitude: number
}

export interface SunDay {
    rise: number | null
    set: number | null
    polar: 'day' | 'night' | null
    noonAltitude: number
    path: Array<SunPathPoint>
    localHour: number
    offsetMinutes: number
}

interface SolarTerms {
    declination: number
    equationOfTime: number
}

const solarTerms = (ms: number): SolarTerms => {
    const t = (ms / DAY_MS + 2440587.5 - 2451545) / 36525
    const l0 = ((280.46646 + t * (36000.76983 + t * 0.0003032)) % 360 + 360) % 360
    const m = 357.52911 + t * (35999.05029 - 0.0001537 * t)
    const e = 0.016708634 - t * (0.000042037 + 0.0000001267 * t)
    const c = Math.sin(m * RAD) * (1.914602 - t * (0.004817 + 0.000014 * t))
        + Math.sin(2 * m * RAD) * (0.019993 - 0.000101 * t)
        + Math.sin(3 * m * RAD) * 0.000289
    const omega = 125.04 - 1934.136 * t
    const lambda = l0 + c - 0.00569 - 0.00478 * Math.sin(omega * RAD)
    const epsilon0 = 23 + (26 + (21.448 - t * (46.815 + t * (0.00059 - t * 0.001813))) / 60) / 60
    const epsilon = epsilon0 + 0.00256 * Math.cos(omega * RAD)
    const declination = Math.asin(Math.sin(epsilon * RAD) * Math.sin(lambda * RAD)) / RAD
    const y = Math.tan((epsilon * RAD) / 2) * Math.tan((epsilon * RAD) / 2)
    const L = l0 * RAD
    const M = m * RAD
    const equationOfTime = (4 / RAD) * (
        y * Math.sin(2 * L)
        - 2 * e * Math.sin(M)
        + 4 * e * y * Math.sin(M) * Math.cos(2 * L)
        - 0.5 * y * y * Math.sin(4 * L)
        - 1.25 * e * e * Math.sin(2 * M)
    )

    return { declination, equationOfTime }
}

/**
 * Altitude above the horizon (with a simple refraction correction) and compass azimuth.
 */
export const sunPosition = (ms: number, lat: number, lon: number): SunPosition => {
    const { declination, equationOfTime } = solarTerms(ms)
    const date = new Date(ms)
    const utcMinutes = date.getUTCHours() * 60 + date.getUTCMinutes() + date.getUTCSeconds() / 60
    const solarMinutes = (((utcMinutes + equationOfTime + 4 * lon) % 1440) + 1440) % 1440
    const hourAngle = solarMinutes / 4 - 180
    const phi = lat * RAD
    const delta = declination * RAD
    const H = hourAngle * RAD
    const cosZenith = Math.min(1, Math.max(-1, Math.sin(phi) * Math.sin(delta) + Math.cos(phi) * Math.cos(delta) * Math.cos(H)))
    const elevation = 90 - Math.acos(cosZenith) / RAD
    const azimuth = ((Math.atan2(Math.sin(H), Math.cos(H) * Math.sin(phi) - Math.tan(delta) * Math.cos(phi)) / RAD + 180) % 360 + 360) % 360

    let refraction = 0

    if (elevation > -0.575 && elevation < 85) {
        const te = Math.tan(elevation * RAD)

        refraction = elevation > 5
            ? (58.1 / te - 0.07 / (te * te * te) + 0.000086 / Math.pow(te, 5)) / 3600
            : (1735 + elevation * (-518.2 + elevation * (103.4 + elevation * (-12.79 + elevation * 0.711)))) / 3600
    }
    else if (elevation <= -0.575) {
        refraction = -20.772 / Math.tan(elevation * RAD) / 3600
    }

    return { altitude: elevation + refraction, azimuth }
}

/**
 * Minutes east of UTC for a time zone at a given instant, from the zone's own calendar fields.
 */
export const zoneOffsetMinutes = (ms: number, timeZone: string): number => {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
    }).formatToParts(new Date(ms))
    const pick = (type: string): number => Number(parts.find(part => part.type === type)?.value ?? 0)
    const local = Date.UTC(pick('year'), pick('month') - 1, pick('day'), pick('hour') % 24, pick('minute'), pick('second'))

    return Math.round((local - Math.floor(ms / 1000) * 1000) / 60000)
}

/**
 * Sunrise, sunset and the altitude curve for the local calendar day in the given city.
 */
export const sunDay = (ms: number, lat: number, lon: number, timeZone: string): SunDay => {
    const offsetMinutes = zoneOffsetMinutes(ms, timeZone)
    const localMs = ms + offsetMinutes * 60000
    const localDate = new Date(localMs)
    const localMidnightUtc = Date.UTC(localDate.getUTCFullYear(), localDate.getUTCMonth(), localDate.getUTCDate()) - offsetMinutes * 60000
    const localHour = (localMs - (localMidnightUtc + offsetMinutes * 60000)) / 3600000

    // Solar noon of this calendar day, refined once with the terms evaluated at noon itself.
    const dayStartUtc = Date.UTC(localDate.getUTCFullYear(), localDate.getUTCMonth(), localDate.getUTCDate())
    let terms = solarTerms(dayStartUtc + DAY_MS / 2)
    let noon = dayStartUtc + (720 - 4 * lon - terms.equationOfTime) * 60000

    terms = solarTerms(noon)
    noon = dayStartUtc + (720 - 4 * lon - terms.equationOfTime) * 60000

    const phi = lat * RAD
    const delta = terms.declination * RAD
    const cosH0 = (Math.sin(HORIZON * RAD) - Math.sin(phi) * Math.sin(delta)) / (Math.cos(phi) * Math.cos(delta))

    let rise: number | null = null
    let set: number | null = null
    let polar: 'day' | 'night' | null = null

    if (cosH0 > 1) polar = 'night'
    else if (cosH0 < -1) polar = 'day'
    else {
        const h0Minutes = (Math.acos(cosH0) / RAD) * 4

        rise = noon - h0Minutes * 60000
        set = noon + h0Minutes * 60000
    }

    const path: Array<SunPathPoint> = []

    for (let minute = 0; minute <= 1440; minute += 15) {
        path.push({ hour: minute / 60, altitude: sunPosition(localMidnightUtc + minute * 60000, lat, lon).altitude })
    }

    return {
        rise,
        set,
        polar,
        noonAltitude: sunPosition(noon, lat, lon).altitude,
        path,
        localHour,
        offsetMinutes
    }
}

/**
 * HH:MM in the city's own time.
 */
export const formatZoneTime = (ms: number, offsetMinutes: number): string => {
    const local = new Date(ms + offsetMinutes * 60000)

    return `${String(local.getUTCHours()).padStart(2, '0')}:${String(local.getUTCMinutes()).padStart(2, '0')}`
}
